// Paged A4 preview of the PDF report the server renders: each sheet shows a page of the PDF (pdf.js). Toolbar:
// previous/next page and page number, zoom (− / list / +), fit width / page, continuous / single / two pages, black and
// white. A new PDF replaces the pages once loaded, keeping the current page and the scroll position.
import cx from "classnames";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type ReactNode,
} from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { Button } from "../../../components/ds2/Button";
import { ArrowLeftIcon, ArrowRightIcon, MinusIcon, PlusIcon } from "../../../components/ds2/icons";
import { LoadingSpinner } from "../../../components/ds2/LoadingSpinner";
import { Numeric } from "../../../components/ds2/Numeric";
import { Select } from "../../../components/ds2/Select";
import useI18n from "../../../hooks/useTranslations";
import ReportPdfPage from "./ReportPdfPage";
import { loadReportPdf } from "./reportPdf";
import { useNumberDraft } from "./useNumberDraft";
import styles from "./ReportPreview.module.css";

export type ReportPreviewProps = {
  pdfUrl: string | null; // object URL of the report PDF; null while the first one is being generated
  className?: string;
};

type Mode = "cont" | "one" | "two";
type Zoom = "width" | "page" | "custom";

// sheet size (A4 at 96 dpi), gap between sheets [px]
const SW = 794,
  SH = 1123,
  GAP = 16;
const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4];
const FIXED_ZOOMS = [50, 75, 100, 125, 150, 200, 300];

const Icon = ({ d }: { d: ReactNode }) => (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    {d}
  </svg>
);

const ReportPreview: FC<ReportPreviewProps> = ({ pdfUrl, className }) => {
  const t = useI18n().translation.screwConnectorModule.calculation.report.viewer;
  const scrollRef = useRef<HTMLDivElement>(null);
  const sheetRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [mode, setMode] = useState<Mode>("cont");
  const [zoom, setZoom] = useState<Zoom>("width");
  const [zoomVal, setZoomVal] = useState(1);
  const [cur, setCur] = useState(1);
  const [bw, setBw] = useState(false);
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  // the page number field keeps what is typed while focused (scrolling changes `cur` without moving the view)
  const draft = useNumberDraft();
  // page to bring into view after the next render (null = keep the scroll position)
  const [scrollReq, setScrollReq] = useState<{ page: number } | null>(null);
  const n = doc?.numPages ?? 0;

  // a new PDF: its pages replace the shown ones once loaded; the replaced document is released
  useEffect(() => {
    if (pdfUrl == null) return;
    let cancelled = false;
    loadReportPdf(pdfUrl)
      .then((loaded) => {
        if (cancelled) {
          void loaded.loadingTask.destroy();
          return;
        }
        setDoc(loaded);
        setCur((c) => Math.min(c, loaded.numPages));
      })
      .catch(() => {
        // the previous pages stay
      });
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);
  useEffect(
    () => () => {
      void doc?.loadingTask.destroy();
    },
    [doc],
  );

  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const measure = () => setBox({ w: sc.clientWidth, h: sc.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sc);
    return () => ro.disconnect();
  }, []);

  const fit = useMemo(() => {
    const cols = mode === "two" ? 2 : 1;
    const fw = Math.max(0.1, (box.w - 32 - (cols - 1) * GAP) / (cols * SW));
    return { w: fw, p: Math.max(0.1, Math.min(fw, (box.h - 32) / SH)) };
  }, [box, mode]);
  const z = zoom === "width" ? fit.w : zoom === "page" ? fit.p : zoomVal;

  const goto = useCallback(
    (p: number, scroll = true) => {
      const next = Math.min(Math.max(1, Math.trunc(p) || 1), Math.max(n, 1));
      setCur(next);
      setScrollReq(scroll || mode === "one" ? { page: next } : null);
    },
    [n, mode],
  );

  useLayoutEffect(() => {
    if (!scrollReq) return;
    const sc = scrollRef.current;
    setScrollReq(null);
    if (!sc) return;
    if (mode === "one") {
      sc.scrollTop = 0;
      return;
    }
    const s = sheetRefs.current[scrollReq.page - 1];
    if (s) sc.scrollTop += s.getBoundingClientRect().top - sc.getBoundingClientRect().top - 16;
  }, [scrollReq, mode]);

  const onScroll = () => {
    const sc = scrollRef.current;
    if (mode === "one" || !sc) return;
    const lim = sc.getBoundingClientRect().top + sc.clientHeight * 0.35;
    let next = 1;
    sheetRefs.current.slice(0, n).forEach((s, i) => {
      if (s && s.getBoundingClientRect().top <= lim) next = i + 1;
    });
    if (next !== cur) setCur(next);
  };

  const step = (dir: 1 | -1) => {
    const nz =
      dir > 0
        ? (ZOOM_STEPS.find((s) => s > z + 0.01) ?? 4)
        : ([...ZOOM_STEPS].reverse().find((s) => s < z - 0.01) ?? 0.25);
    setZoom("custom");
    setZoomVal(nz);
  };

  const onZoomSelect = (v: string) => {
    if (v === "width" || v === "page") setZoom(v);
    else if (v !== "custom") {
      setZoomVal(parseInt(v, 10) / 100);
      setZoom("custom");
    }
  };

  const changeMode = (m: Mode) => {
    setMode(m);
    setScrollReq({ page: cur });
  };

  const pc = (v: number) => Math.round(v * 100);
  const zoomOptions = [
    { value: "width", label: t.fitWidthOption(pc(fit.w)) },
    { value: "page", label: t.fitPageOption(pc(fit.p)) },
    ...(zoom === "custom" ? [{ value: "custom", label: `${pc(zoomVal)} %` }] : []),
    ...FIXED_ZOOMS.map((v) => ({ value: String(v), label: `${v} %` })),
  ];

  return (
    <div className={cx(styles.viewer, className)} data-tut="pv-root">
      <div className={styles.bar} role="toolbar" aria-label={t.toolbar}>
        <div className={styles.group}>
          <Button
            variant="plain"
            title={t.previousPage}
            aria-label={t.previousPage}
            disabled={cur <= 1}
            onClick={() => goto(cur - 1)}
          >
            <ArrowLeftIcon width={16} height={16} />
          </Button>
          <Numeric
            aria-label={t.pageNumber}
            width="46px"
            decimalScale={0}
            {...draft("page", cur, (p) => p !== null && goto(p), { allowEmpty: false })}
          />
          <span className={styles.of}>/ {n}</span>
          <Button
            variant="plain"
            title={t.nextPage}
            aria-label={t.nextPage}
            disabled={cur >= n}
            onClick={() => goto(cur + 1)}
          >
            <ArrowRightIcon width={16} height={16} />
          </Button>
        </div>
        <div className={styles.group}>
          <Button variant="plain" title={t.zoomOut} aria-label={t.zoomOut} onClick={() => step(-1)}>
            <MinusIcon width={16} height={16} />
          </Button>
          <div className={styles.zoom}>
            <Select
              aria-label={t.zoom}
              options={zoomOptions}
              value={zoomOptions.find((o) => o.value === zoom) ?? null}
              isSearchable={false}
              onChange={(o) => o && onZoomSelect(o.value)}
            />
          </div>
          <Button variant="plain" title={t.zoomIn} aria-label={t.zoomIn} onClick={() => step(1)}>
            <PlusIcon width={16} height={16} />
          </Button>
        </div>
        <div className={styles.group}>
          <button
            type="button"
            className={styles.btn}
            title={t.fitWidth}
            aria-pressed={zoom === "width"}
            onClick={() => setZoom("width")}
          >
            <Icon
              d={
                <>
                  <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" />
                  <path d="M4.5 8h7M6.5 6l-2 2 2 2M9.5 6l2 2-2 2" />
                </>
              }
            />
          </button>
          <button
            type="button"
            className={styles.btn}
            title={t.fitPage}
            aria-pressed={zoom === "page"}
            onClick={() => setZoom("page")}
          >
            <Icon d={<path d="M4 1.5h5.5L12.5 4.5v10H4zM9.5 1.5v3h3" />} />
          </button>
        </div>
        <div className={styles.group}>
          <button
            type="button"
            className={styles.btn}
            title={t.continuous}
            aria-pressed={mode === "cont"}
            onClick={() => changeMode("cont")}
          >
            <Icon
              d={
                <>
                  <rect x="4" y="1.5" width="8" height="5.5" rx="1" />
                  <rect x="4" y="9" width="8" height="5.5" rx="1" />
                </>
              }
            />
          </button>
          <button
            type="button"
            className={styles.btn}
            title={t.singlePage}
            aria-pressed={mode === "one"}
            onClick={() => changeMode("one")}
          >
            <Icon d={<rect x="4.5" y="1.5" width="7" height="13" rx="1" />} />
          </button>
          <button
            type="button"
            className={styles.btn}
            title={t.twoPages}
            aria-pressed={mode === "two"}
            onClick={() => changeMode("two")}
          >
            <Icon
              d={
                <>
                  <rect x="1.5" y="3" width="6" height="10" rx="1" />
                  <rect x="8.5" y="3" width="6" height="10" rx="1" />
                </>
              }
            />
          </button>
        </div>
        <div className={styles.group}>
          <button
            type="button"
            className={styles.btn}
            title={t.blackWhite}
            aria-pressed={bw}
            onClick={() => setBw((b) => !b)}
          >
            <Icon
              d={
                <>
                  <circle cx="8" cy="8" r="6" />
                  <path d="M8 2a6 6 0 010 12z" fill="currentColor" />
                </>
              }
            />
            {t.blackWhiteShort}
          </button>
        </div>
      </div>
      <div ref={scrollRef} className={styles.scroll} onScroll={onScroll} data-tut="pv-scroll">
        <div
          className={cx(styles.pages, mode === "two" && styles.two, bw && styles.bw)}
          style={{ "--pvz": z } as CSSProperties}
        >
          {!doc ? (
            <div className={styles.busy}>
              <LoadingSpinner label={t.paginating} />
            </div>
          ) : (
            Array.from({ length: n }, (_, i) => (
              <div
                key={i}
                ref={(el) => {
                  sheetRefs.current[i] = el;
                }}
                className={cx(styles.sheet, mode === "one" && i + 1 !== cur && styles.hide)}
                data-tut="pv-sheet"
              >
                <ReportPdfPage doc={doc} pageNumber={i + 1} className={styles.page} />
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;
